import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Tables } from '@/integrations/supabase/types';

// We need to manually extend the Profile type to include the new fields
// since they're not yet reflected in the generated types
export type Profile = Tables<'profiles'> & {
  company?: string | null;
  phone?: string | null;
  website?: string | null;
  email_notifications?: boolean;
  project_updates?: boolean;
  marketing_emails?: boolean;
  // Stripe-related fields
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  plan_key?: string;
  content_used?: number;
  content_quota?: number | null;
  projects_limit?: number | null;
  quota_resets_at?: string | null;
  feature_flags?: Record<string, boolean>;
  account_state?: string;
  used_free_reddit_post?: boolean;
};

// Make sure ProfileUpdate includes all the fields we're trying to update
export interface ProfileUpdate extends Partial<Omit<Profile, 'id' | 'created_at'>> {
  updated_at?: string;
  company?: string | null;
  phone?: string | null;
  website?: string | null;
  email_notifications?: boolean;
  project_updates?: boolean;
  marketing_emails?: boolean;
  // Stripe-related fields shouldn't be directly updatable from the frontend
  // They are managed by the Stripe webhook and backend functions
}

// Constants for validation
const MAX_FIELD_LENGTHS = {
  first_name: 100,
  last_name: 100,
  company: 100,
  phone: 20,
  website: 200
};

const MAX_AVATAR_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export function useProfile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);

  // Validate profile fields
  const validateProfileFields = (updates: ProfileUpdate): { valid: boolean; message?: string } => {
    // Check for max length of text fields
    for (const [key, value] of Object.entries(updates)) {
      if (typeof value === 'string' && MAX_FIELD_LENGTHS[key as keyof typeof MAX_FIELD_LENGTHS]) {
        const maxLength = MAX_FIELD_LENGTHS[key as keyof typeof MAX_FIELD_LENGTHS];
        if (value.length > maxLength) {
          return { 
            valid: false, 
            message: `${key.replace('_', ' ')} must be less than ${maxLength} characters` 
          };
        }
      }
    }

    // Validate website format if present
    if (updates.website && typeof updates.website === 'string' && updates.website.trim() !== '') {
      try {
        // Check if it's a valid URL
        new URL(updates.website);
        
        // Ensure it uses http or https protocol
        if (!updates.website.startsWith('http://') && !updates.website.startsWith('https://')) {
          // Try to prepend https:// and see if it becomes valid
          try {
            new URL(`https://${updates.website}`);
            // If valid, automatically prepend https://
            updates.website = `https://${updates.website}`;
          } catch (e) {
            return { valid: false, message: 'Website must be a valid URL. Try including https://' };
          }
        }
      } catch (e) {
        return { valid: false, message: 'Please enter a valid website URL including https://' };
      }
    }

    // Sanitize phone number if present (basic check)
    if (updates.phone && typeof updates.phone === 'string') {
      // Only allow digits, spaces, parentheses, plus sign, and hyphens in phone numbers
      if (!/^[0-9\s()+-]+$/.test(updates.phone)) {
        return { valid: false, message: 'Phone number contains invalid characters' };
      }
    }

    return { valid: true };
  };

  // Ensure all profile fields exist
  const ensureProfileFields = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error && error.code === 'PGRST116') {
        // Profile doesn't exist, create it with default values (upsert avoids 409 if created elsewhere)
        await supabase.from('profiles').upsert(
          {
            id: userId,
            first_name: user?.user_metadata?.first_name || null,
            last_name: user?.user_metadata?.last_name || null,
            avatar_url: null,
            company: null,
            phone: null,
            website: null,
            email_notifications: true,
            project_updates: true,
            marketing_emails: false,
            plan_key: 'free',
            content_used: 0,
            content_quota: null,
            projects_limit: null,
            feature_flags: {},
            account_state: 'on_free_tier',
            used_free_reddit_post: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'id' }
        );
      } else if (error) {
        console.error("Error fetching profile", "Database error occurred");
        return null;
      } else {
        // Profile exists, check if it has all required fields and add them if not
        const updates: ProfileUpdate = {};
        let needsUpdate = false;
        
        // Define the fields we want to check and their default values
        const fieldDefaults = {
          company: null,
          phone: null,
          website: null,
          email_notifications: true,
          project_updates: true,
          marketing_emails: false,
          // Default Stripe-related fields
          plan_key: 'free',
          content_used: 0,
          account_state: 'on_free_tier',
          used_free_reddit_post: false,
          feature_flags: {}
        };
        
        // Type-safe way to add missing fields
        type ProfileKey = keyof typeof fieldDefaults;
        
        for (const key in fieldDefaults) {
          const profileKey = key as ProfileKey;
          if (data[profileKey] === undefined) {
            const value = fieldDefaults[profileKey];
            // Use unknown as intermediate step for type safety
            (updates as unknown as Record<ProfileKey, typeof value>)[profileKey] = value;
            needsUpdate = true;
          }
        }
        
        // Update the profile if any fields were missing
        if (needsUpdate) {
          await supabase
            .from('profiles')
            .update(updates)
            .eq('id', userId);
        }
      }
    } catch (error) {
      console.error("Unexpected error ensuring profile fields", "An unexpected error occurred");
    }
  };

  // Helper function to detect authentication errors more reliably
  const isAuthError = (error: any): boolean => {
    if (!error) return false;
    
    // Check error codes first (most reliable)
    if (error.code === 'PGRST301' || error.code === '401' || error.code === '403') {
      return true;
    }
    
    // Check specific error messages
    if (error.message) {
      const authErrorPatterns = [
        /JWT/i,
        /session.*expired/i,
        /authentication.*failed/i,
        /invalid.*token/i,
        /unauthorized/i
      ];
      
      return authErrorPatterns.some(pattern => pattern.test(error.message));
    }
    
    return false;
  };

  // Fetch profile data
  const { data: profile, isLoading, error, refetch } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      try {
        // Ensure all required profile fields exist
        await ensureProfileFields(user.id);
        
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (error) {
          // Handle authentication errors gracefully
          if (isAuthError(error)) {
            console.log("Authentication error detected, session may need refresh");
            // Don't throw error, return null to prevent infinite loading states
            return null;
          }
          
          console.error("Error fetching profile", "Database error");
          throw new Error("Could not load profile");
        }
        
        return data as Profile;
      } catch (error) {
        console.error("Error in profile query", "An unexpected error occurred");
        
        // For authentication-related errors, return null instead of throwing
        // This prevents the component from getting stuck in loading states
        if (isAuthError(error)) {
          return null;
        }
        
        throw new Error("Could not load profile data");
      }
    },
    enabled: !!user,
    retry: (failureCount, error) => {
      // Don't retry authentication errors immediately
      if (isAuthError(error)) {
        return false;
      }
      // Retry other errors up to 2 times
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Update profile mutation
  const updateProfile = useMutation({
    mutationFn: async (updates: ProfileUpdate) => {
      if (!user) throw new Error("Authentication required");
      
      // Validate input fields
      const validation = validateProfileFields(updates);
      if (!validation.valid) {
        throw new Error(validation.message || "Invalid profile data");
      }

      try {
        // Check if profile exists before attempting to update
        const { data: existingProfile, error: fetchError } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', user.id)
          .single();
          
        if (fetchError) {
          console.error("Error fetching profile before update:", fetchError);
          
          // If profile doesn't exist, create it first (upsert avoids 409 if created elsewhere)
          if (fetchError.code === 'PGRST116') {
            const { error: upsertError } = await supabase.from('profiles').upsert(
              {
                id: user.id,
                ...updates,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              },
              { onConflict: 'id' }
            );
            
            if (upsertError) {
              console.error("Error creating profile:", upsertError);
              throw new Error(`Failed to create profile: ${upsertError.message}`);
            }
            
            return true;
          }
          
          throw new Error(`Profile fetch error: ${fetchError.message}`);
        }
        
        // If profile exists, update it
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            ...updates,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
        
        if (updateError) {
          console.error("Error updating profile:", updateError);
          throw new Error(`Failed to update profile: ${updateError.message}`);
        }
        
        return true;
      } catch (error: any) {
        console.error("Profile update error:", error);
        throw error instanceof Error ? error : new Error("Failed to update profile");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated.",
      });
    },
    onError: (error: any) => {
      console.error("Error updating profile:", error);
      toast({
        title: "Error updating profile",
        description: error.message || "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Upload avatar
  const uploadAvatar = async (file: File): Promise<string | null> => {
    if (!user) return null;
    
    // Validate file size
    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      toast({
        title: "File too large",
        description: `Avatar must be less than ${MAX_AVATAR_SIZE_BYTES / (1024 * 1024)}MB`,
        variant: "destructive",
      });
      return null;
    }
    
    // Validate file type
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a valid image file (JPEG, PNG, WebP, or GIF)",
        variant: "destructive",
      });
      return null;
    }
    
    setIsUploading(true);
    
    try {
      // Create a unique filename to prevent overwriting and security issues
      const fileExt = file.name.split('.').pop();
      const uniqueId = Date.now().toString() + Math.random().toString(36).substring(2, 9);
      
      // IMPORTANT: Structure the file path to match RLS policy
      // The policy expects the first folder to be the user's ID
      const filePath = `${user.id}/${uniqueId}.${fileExt}`;
      
      console.log("Uploading avatar with path:", filePath);
      
      // Upload the file to the user-avatars bucket
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('user-avatars')
        .upload(filePath, file, {
          cacheControl: '3600', // Cache for 1 hour
          upsert: true // Overwrite if exists to avoid conflicts
        });
      
      if (uploadError) {
        console.error("Error uploading image:", uploadError);
        throw new Error(`Failed to upload image: ${uploadError.message}`);
      }
      
      // Get the public URL
      const { data } = supabase.storage.from('user-avatars').getPublicUrl(filePath);
      
      if (!data || !data.publicUrl) {
        throw new Error("Failed to get public URL for uploaded image");
      }
      
      // Update the user's profile with the new avatar URL
      await updateProfile.mutateAsync({
        avatar_url: data.publicUrl
      });
      
      // Clean up old avatars - optional but good for storage management
      try {
        if (profile?.avatar_url) {
          const oldUrl = profile.avatar_url;
          // Extract the path from the URL
          const oldPathMatch = oldUrl.match(/\/storage\/v1\/object\/public\/user-avatars\/(.*)/);
          if (oldPathMatch && oldPathMatch[1]) {
            const oldPath = oldPathMatch[1];
            // Only delete if it's not the current file
            if (oldPath !== filePath) {
              await supabase.storage
                .from('user-avatars')
                .remove([oldPath]);
            }
          }
        }
      } catch (cleanupError) {
        // Don't throw on cleanup errors, just log them
        console.error("Failed to clean up old avatar:", cleanupError);
      }
      
      toast({
        title: "Avatar updated",
        description: "Your profile picture has been updated successfully.",
      });
      
      return data.publicUrl;
    } catch (error: any) {
      console.error("Error uploading avatar:", error);
      toast({
        title: "Error uploading avatar",
        description: error.message || "Failed to upload avatar. Please try a different image or try again later.",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    profile,
    isLoading,
    error,
    updateProfile: updateProfile.mutate,
    uploadAvatar,
    isUploading,
    refetchProfile: refetch
  };
}
